import { extractTextValue, isObjectRecord } from './object-utils'

const DASHBOARD_ONBOARDING_BLOCK_STATUSES = new Set([403, 409, 412, 423, 428])
const DASHBOARD_ONBOARDING_BLOCK_CODES = new Set([
  'dashboard_access_blocked',
  'onboarding_incomplete',
  'onboarding_required',
  'post_payment_onboarding_required',
])

export class DashboardAccessBlockedError extends Error {
  readonly status: number | undefined
  readonly code: string | null

  constructor(params: {
    message: string
    status?: number
    code?: string | null
  }) {
    super(params.message)
    this.name = 'DashboardAccessBlockedError'
    this.status = params.status
    this.code = params.code ?? null
  }
}

function normalizeCode(value: string | null | undefined): string | null {
  return typeof value === 'string' ? value.trim().toLowerCase() : null
}

async function parseResponseBody(
  response: Response,
): Promise<Record<string, unknown> | string | null> {
  const bodyText = await response.text().catch(() => '')

  if (bodyText.trim().length === 0) {
    return null
  }

  try {
    const parsed: unknown = JSON.parse(bodyText)
    return isObjectRecord(parsed) ? parsed : bodyText
  } catch {
    return bodyText
  }
}

function buildDashboardAccessMessage({
  body,
  fallback,
  status,
  statusText,
}: {
  body: Record<string, unknown> | string | null
  fallback: string
  status?: number
  statusText?: string
}): string {
  if (typeof body === 'string' && body.trim().length > 0) {
    return body
  }

  if (isObjectRecord(body)) {
    const candidates = [body.message, body.error, body.title, body.detail].map(
      (value) => extractTextValue(value),
    )
    const resolved = candidates.find((value) => value != null)
    if (resolved != null) {
      return resolved
    }
  }

  if (status != null && statusText != null && statusText.length > 0) {
    return `${fallback} (${status} ${statusText})`
  }

  return fallback
}

function extractDashboardAccessCode(
  body: Record<string, unknown> | string | null,
): string | null {
  if (!isObjectRecord(body)) {
    return null
  }

  return normalizeCode(
    extractTextValue(body.code) ??
      extractTextValue(body.errorCode) ??
      extractTextValue(body.error) ??
      extractTextValue(body.type),
  )
}

function isOnboardingBlockedMessage(message: string): boolean {
  const normalizedMessage = message.toLowerCase()

  return (
    normalizedMessage.includes('onboarding') &&
    (normalizedMessage.includes('required') ||
      normalizedMessage.includes('incomplete') ||
      normalizedMessage.includes('complete'))
  )
}

export async function toDashboardAccessBlockedError(
  error: unknown,
): Promise<DashboardAccessBlockedError | null> {
  if (error instanceof DashboardAccessBlockedError) {
    return error
  }

  if (error instanceof Response) {
    const body = await parseResponseBody(error.clone())
    const code = extractDashboardAccessCode(body)
    const message = buildDashboardAccessMessage({
      body,
      fallback: 'Dashboard access is blocked until onboarding is completed.',
      status: error.status,
      statusText: error.statusText,
    })

    if (
      DASHBOARD_ONBOARDING_BLOCK_STATUSES.has(error.status) &&
      (code != null && DASHBOARD_ONBOARDING_BLOCK_CODES.has(code)
        ? true
        : isOnboardingBlockedMessage(message))
    ) {
      return new DashboardAccessBlockedError({
        code,
        message,
        status: error.status,
      })
    }

    return null
  }

  if (isObjectRecord(error)) {
    const status =
      typeof error.status === 'number' && Number.isFinite(error.status)
        ? error.status
        : undefined
    const body = isObjectRecord(error.body)
      ? error.body
      : (extractTextValue(error.body) ?? null)
    const code = extractDashboardAccessCode(body)
    const message = buildDashboardAccessMessage({
      body,
      fallback:
        extractTextValue(error.message) ??
        'Dashboard access is blocked until onboarding is completed.',
      status,
      statusText: extractTextValue(error.statusText) ?? undefined,
    })

    if (
      status != null &&
      DASHBOARD_ONBOARDING_BLOCK_STATUSES.has(status) &&
      (code != null && DASHBOARD_ONBOARDING_BLOCK_CODES.has(code)
        ? true
        : isOnboardingBlockedMessage(message))
    ) {
      return new DashboardAccessBlockedError({
        code,
        message,
        status,
      })
    }
  }

  return null
}

export function isDashboardAccessBlockedError(
  error: unknown,
): error is DashboardAccessBlockedError {
  return error instanceof DashboardAccessBlockedError
}
